import React, {useCallback, useMemo, useState} from 'react';
import {useQuery} from 'react-query';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {
  DashboardLayout,
  SearchInput,
  SecondaryButton,
} from '../../../components';
import {baasMobile, green, netFlow, red} from '../../../assets/images/images';
import {
  fetchBAASTransactions,
  fetchBAASTransactionsSummary,
} from '../../../requests/queries/baas';
import BaasTransactionModal from './BaasTransactionModal';
import {useBaasExport} from './hooks/useBAASExport';
import {useBaasSettlement} from './hooks/useBaasSettlement';
import {typeAndStatusFilterOptions} from './constants';
import {usePermissions} from '../../../hooks/useSidbarPermissions';
import CardSummary from 'components/CardSummary';
import BaasTransactionTable from './components/BaasTransactionTable';
import {shortenLargeNumbers} from 'helpers/shortenLargeNumbers';
import AppliedFilters from 'components/AppliedFilters';

const initialFilters = {
  start: '',
  end: '',
};

function BaasTransactions() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(initialFilters);
  const [openFilter, setOpenFilter] = useState(false);
  const [transaction, setTransaction] = useState({});
  const [transactionDetails, setTransactionDetails] = useState(false);
  const [searchText, setSearchText] = useState('');
  const {userDetails} = useSelector(state => state?.auth);

  const [query, setQuery] = useState({
    page: 1,
    length: 10,
  });
  const {user_account} = useBaasSettlement();
  const [filterProps, setFilterProps] = useState(null);
  const [filtersApplied, setFiltersApplied] = useState(false);

  const toggleTransactionsModal = useCallback(transaction => {
    setTransactionDetails(prevState => !prevState);
    setTransaction(transaction);
  }, []);

  const {
    baasPagePermissions: {canTransfer, canExportTransactions},
  } = usePermissions();

  const params = useMemo(
    () => ({
      company_id: userDetails && userDetails?.company?.id,
      ...query,
      search: searchText,
      start_date: filters?.start,
      end_date: filters?.end,
      status: filters?.transaction_status?.toString()?.toUpperCase(),
      transaction_type: filters?.transaction_type?.toString()?.toLowerCase(),
    }),
    [
      filters?.end,
      filters?.start,
      query,
      searchText,
      userDetails,
      filters?.transaction_status,
      filters?.transaction_type,
    ],
  );

  const summaryParams = useMemo(
    () => ({
      company_id: userDetails && userDetails?.company?.id,
      start_date: filters?.start,
      end_date: filters?.end,
      status: filters?.transaction_status?.toString()?.toUpperCase(),
      transaction_type: filters?.transaction_type?.toString()?.toLowerCase(),
    }),
    [
      userDetails,
      filters?.start,
      filters?.end,
      filters?.transaction_status,
      filters?.transaction_type,
    ],
  );

  const {handleExport, exportData} = useBaasExport({
    params,
    apiFunc: fetchBAASTransactions,
  });

  const {data: baas_transactions, isLoading} = useQuery(
    ['baas-transactions', {params}],
    () => fetchBAASTransactions({params}),
    {
      retry: false,
      meta: {preventGlobalError: true},
      enabled: !exportData,
    },
  );

  const {data: baas_transactions_summary, isLoading: summaryLoading} = useQuery(
    ['baas-transaction-summary', {summaryParams}],
    () => fetchBAASTransactionsSummary({params: summaryParams}),
    {enabled: !exportData},
  );

  const transactions = useMemo(
    () =>
      !isLoading &&
      baas_transactions &&
      baas_transactions?.entity?.transactions,
    [baas_transactions, isLoading],
  );

  const handlePageChange = page => {
    setQuery(prev => ({...prev, page}));
    const searchParams = new URLSearchParams(document.location.search);
    searchParams.set('page', page);
    const newSearchParams = searchParams.toString();
    navigate(`?${newSearchParams}`);
  };

  const handleLimitChange = length => {
    setQuery(prev => ({...prev, length, page: 1}));
  };

  const handleResetFilter = () => {
    navigate('');
    setFilters(initialFilters);
    setOpenFilter(true);
    setSearchText('');
    setFiltersApplied(false);
  };

  const handleFilterStateChange = props => {
    setFilterProps(props);
    setFiltersApplied(
      Object.values(filters).some(value => value) || searchText.length > 0,
    );
  };

  const isNotFiltered = useMemo(
    () => !isLoading && (!transactions || transactions?.length === 0),
    [transactions, isLoading],
  );

  const summaryData = useMemo(
    () =>
      (!summaryLoading &&
        baas_transactions_summary &&
        baas_transactions_summary?.entity) ||
      {},
    [baas_transactions_summary, summaryLoading],
  );

  const formattedSummary = useMemo(
    () => ({
      deposit: shortenLargeNumbers(summaryData.deposit || 0, 'NGN', {
        startAt: 'B',
      }),
      transfer: shortenLargeNumbers(summaryData.transfer || 0, 'NGN', {
        startAt: 'B',
      }),
      net: shortenLargeNumbers(summaryData.net || 0, 'NGN', {startAt: 'B'}),
    }),
    [summaryData],
  );

  return (
    <DashboardLayout
      bg="bg-white80"
      topPadding="pt-8"
      xlLeftMargin="xl:ml-12"
      breadCrumbs={
        <div className="flex items-center">
          <img src={baasMobile} alt="" />
          <p className="ml-2 -mb-1">Transactions</p>
        </div>
      }
    >
      <div className="flex items-center gap-4 flex-wrap">
        <p className="text-grey font-medium text-[18px]">Transactions</p>
        {filtersApplied && filterProps && (
          <AppliedFilters {...filterProps} isMainPage={true} />
        )}
      </div>
      <div className="flex mt-8 items-start lg:items-center lg:flex-row flex-col gap-5 justify-between p-5 lg:p-0">
        <div className="flex items-center gap-2">
          <SearchInput
            onChange={value => setSearchText(value)}
            className="sm:w-[277px] rounded-lg outline-[#eceff3]"
          />
          <BaasTransactionTable.FilterButton
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            className="h-[48px] justify-center w-[130px]"
          />
        </div>
        <div className="flex items-center gap-2">
          <SecondaryButton
            buttonText="Export"
            className="w-[121px]"
            loading={exportData}
            onClick={handleExport}
            disabled={!canExportTransactions}
          />
        </div>
      </div>

      <BaasTransactionModal
        show={transactionDetails}
        onClose={toggleTransactionsModal}
        transaction={transaction}
      />

      <div className="mt-8">
        <>
          <div className="mt-6 grid lg:grid-cols-3 gap-4">
            <CardSummary
              img={green}
              title="Total Collections"
              body={formattedSummary.deposit || '₦0'}
              className="!bg-white !px-6 !py-8"
              loading={summaryLoading}
            />
            <CardSummary
              img={netFlow}
              title="Total Payout"
              body={formattedSummary.transfer || '₦0'}
              className="!bg-white !px-6 !py-8"
              loading={summaryLoading}
            />
            <CardSummary
              img={red}
              title="Estimated Settlements"
              body={formattedSummary.settlement || '₦0'}
              className="!bg-white !px-6 !py-8"
              loading={summaryLoading}
            />
          </div>
        </>

        <BaasTransactionTable
          transactions={transactions || []}
          isLoading={isLoading}
          onViewTransaction={toggleTransactionsModal}
          className="mt-6"
          totalPages={baas_transactions?.entity?.total_pages || 1}
          currentPage={Number(baas_transactions?.entity?.current_page) || 1}
          onPageChange={handlePageChange}
          limit={query.length}
          onLimitChange={handleLimitChange}
          totalItems={baas_transactions?.entity?.current_page || 0}
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
          filters={filters}
          setFilters={setFilters}
          filterOptions={typeAndStatusFilterOptions}
          onResetFilter={handleResetFilter}
          navigate={navigate}
          searchText={searchText}
          setSearchText={setSearchText}
          initialFilters={initialFilters}
          onFilterStateChange={handleFilterStateChange}
        />
      </div>
    </DashboardLayout>
  );
}

export default BaasTransactions;
